import React, { useState, useEffect } from 'react';
import SLPManagementPage from '../SLP/SLPManagementPage';
import SLPEventPage from '../SLP/SLPEventPage';
import {
  schoolEvents,
  academicCalendar,
  holidayCalendar,
  eventCategories,
  eventTypes,
  getEventsByMonth,
  getEventsByDate,
  getCycleDayForDate
} from '../../Data/schoolCalendar';

// School Event Page Component
const SchoolEventPage = ({ schoolEvents, onBack, onTabChange, formatDate }) => {
  const [currentYear, setCurrentYear] = useState(2025);
  const [currentMonth, setCurrentMonth] = useState(5); // June (0-indexed)
  const [selectedEventCategory, setSelectedEventCategory] = useState('All');
  const [selectedEventType, setSelectedEventType] = useState('All');
  const [selectedMonths, setSelectedMonths] = useState('All');
  const [showSLPPage, setShowSLPPage] = useState(false);
  const [showSLPManagement, setShowSLPManagement] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [navigationSource, setNavigationSource] = useState(null);

  useEffect(() => {
    // Check if user came from class dashboard
    const source = localStorage.getItem('navigationSource');
    if (source) {
      try {
        const sourceInfo = JSON.parse(source);
        setNavigationSource(sourceInfo);
      } catch (e) {
        console.error('Error parsing navigation source:', e);
      }
    }
  }, []);

  const handleBackNavigation = () => {
    if (navigationSource?.source === 'class-dashboard' && navigationSource?.returnTo === 'teacher-directory') {
      // Go back to teacher directory (which shows class dashboard)
      // Use the onTabChange function to navigate to teacher-directory tab
      onTabChange('teacher-directory');
      // Clear the navigation source
      localStorage.removeItem('navigationSource');
    } else {
      // Default back behavior
      onBack();
    }
  };

  // Get comprehensive school events data
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  
  // Filter events based on selections
  const getFilteredEvents = () => {
    let filtered = allEvents;
    
    if (selectedEventCategory !== 'All') {
      filtered = filtered.filter(event => event.category === selectedEventCategory);
    }
    
    if (selectedEventType !== 'All') {
      filtered = filtered.filter(event => event.type === selectedEventType);
    }
    
    if (selectedMonths !== 'All') {
      const monthIndex = monthOptions.indexOf(selectedMonths) - 1; // -1 because 'All' is first
      if (monthIndex >= 0) {
        const monthStr = `${currentYear}-${String(monthIndex + 1).padStart(2, '0')}`;
        filtered = filtered.filter(event => event.date.startsWith(monthStr));
      }
    }
    
    return filtered;
  };

  const categoryOptions = ['All', ...Object.keys(eventCategories)];
  const typeOptions = ['All', ...Object.keys(eventTypes)];
  const monthOptions = ['All', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const getEventsForDate = (date) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    const dayEvents = getEventsByDate(dateStr);
    const cycleDay = getCycleDayForDate(dateStr);
    
    // Add cycle day information if it's a school day
    if (cycleDay && !dayEvents.some(event => event.category === 'holiday')) {
      dayEvents.unshift({
        id: `cycle_${dateStr}`,
        title: cycleDay.name,
        type: 'cycle',
        category: 'academic',
        color: cycleDay.color,
        isCycleDay: true
      });
    }
    
    return dayEvents;
  };

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    if (!day) return;
    
    const clickedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(clickedDate);
    setShowSLPPage(true);
  };

  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
  const dayNames = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const calendarDays = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Show SLP Management Page
  if (showSLPManagement) {
    return (
      <SLPManagementPage 
        onBack={() => setShowSLPManagement(false)} 
      />
    );
  }

  // Show SLP Event Page if date is selected
  if (showSLPPage && selectedDate) {
    return (
      <SLPEventPage 
        selectedDate={selectedDate} 
        onBack={() => setShowSLPPage(false)} 
      />
    );
  }

  return (
    <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
      {/* Header with return button */}
      <div className="page-header">
        <button className="back-button" onClick={handleBackNavigation}>
          {navigationSource?.source === 'class-dashboard' ? '← 返回課室管理' : '← 返回主頁'}
        </button>
        <h1>學校活動</h1>
      </div>

      {/* Header Controls */}
      <div style={{
        backgroundColor: '#dc2626',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <select 
          value={currentYear}
          onChange={(e) => setCurrentYear(parseInt(e.target.value))}
          style={{
            padding: '6px 8px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'white',
            color: '#333',
            fontSize: '14px'
          }}
        >
          <option value={2024}>YEAR 2024</option>
          <option value={2025}>YEAR 2025</option>
          <option value={2026}>YEAR 2026</option>
        </select>

        <div style={{
          backgroundColor: '#10b981',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          📚 校曆表
        </div>

        <select 
          value={selectedEventCategory}
          onChange={(e) => setSelectedEventCategory(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'white',
            color: '#333',
            fontSize: '14px'
          }}
        >
          {categoryOptions.map(category => (
            <option key={category} value={category}>
              類別: {category === 'All' ? '全部' : eventCategories[category]?.name || category}
            </option>
          ))}
        </select>

        <select 
          value={selectedEventType}
          onChange={(e) => setSelectedEventType(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'white',
            color: '#333',
            fontSize: '14px'
          }}
        >
          {typeOptions.map(type => (
            <option key={type} value={type}>
              類型: {type === 'All' ? '全部' : eventTypes[type]?.name || type}
            </option>
          ))}
        </select>

        <select 
          value={selectedMonths}
          onChange={(e) => setSelectedMonths(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'white',
            color: '#333',
            fontSize: '14px'
          }}
        >
          {monthOptions.map(month => (
            <option key={month} value={month}>Months: {month}</option>
          ))}
        </select>

        {/* SLP Management Button */}
        <button
          onClick={() => setShowSLPManagement(true)}
          style={{
            padding: '6px 12px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: '#059669',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          📊 SLP管理
        </button>

        <div style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: '600',
          marginLeft: 'auto',
          textAlign: 'center'
        }}>
          {new Date().getDate()}<br/>
          <span style={{fontSize: '12px'}}>
            {['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'][new Date().getMonth()]}
          </span>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <button 
          onClick={goToPreviousMonth}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ◀
        </button>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0
        }}>
          {currentYear}年{monthNames[currentMonth]}
        </h2>
        <button 
          onClick={goToNextMonth}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ▶
        </button>
      </div>

      {/* Calendar Grid */}
      <div style={{backgroundColor: 'white', padding: '0 16px'}}>
        {/* Day Headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          backgroundColor: '#f3f4f6',
          padding: '12px 0'
        }}>
          {dayNames.map(day => (
            <div key={day} style={{
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '600',
              color: '#6b7280',
              padding: '8px 4px'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          backgroundColor: '#f3f4f6'
        }}>
          {calendarDays.map((day, index) => {
            const dayEvents = day ? getEventsForDate(day) : [];
            const hasEvents = dayEvents.length > 0;
            const importantEvents = dayEvents.filter(event => !event.isCycleDay);
            const hasImportantEvents = importantEvents.length > 0;
            
            // Get the most important event for label
            const labelEvent = importantEvents.find(event => 
              event.category === 'holiday' || 
              event.category === 'examination' ||
              event.type === 'ceremony'
            ) || importantEvents[0];
            
            return (
              <div key={index} style={{
                backgroundColor: 'white',
                minHeight: '80px',
                padding: '4px',
                position: 'relative',
                border: day ? '1px solid #e5e7eb' : 'none',
                cursor: day ? 'pointer' : 'default'
              }}
              onClick={() => handleDateClick(day)}>
                {day && (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1f2937'
                      }}>
                        {day}
                      </div>
                      {/* Label caption for special events */}
                      {hasImportantEvents && labelEvent && (
                        <div style={{
                          fontSize: '8px',
                          fontWeight: '600',
                          color: eventCategories[labelEvent.category]?.color || '#6b7280',
                          backgroundColor: `${eventCategories[labelEvent.category]?.color || '#6b7280'}15`,
                          padding: '1px 3px',
                          borderRadius: '2px',
                          maxWidth: '40px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                          lineHeight: '1.2'
                        }}>
                          {labelEvent.category === 'holiday' ? '假期' :
                           labelEvent.category === 'examination' ? '考試' :
                           labelEvent.type === 'ceremony' ? '典禮' :
                           labelEvent.type === 'competition' ? '比賽' :
                           labelEvent.type === 'meeting' ? '會議' :
                           labelEvent.category === 'cultural' ? '文化' :
                           labelEvent.category === 'sports' ? '體育' :
                           labelEvent.category === 'parent' ? '家長' :
                           labelEvent.category === 'community' ? '服務' :
                           '活動'}
                        </div>
                      )}
                    </div>
                    
                    {dayEvents.map((event, eventIndex) => {
                      const categoryInfo = eventCategories[event.category] || {};
                      const eventColor = event.color || categoryInfo.color || '#6b7280';
                      
                      return (
                        <div key={event.id} style={{
                          backgroundColor: eventColor,
                          color: 'white',
                          fontSize: '9px',
                          padding: '1px 3px',
                          borderRadius: '2px',
                          marginBottom: '1px',
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          opacity: event.isCycleDay ? 0.7 : 1
                        }}>
                          {event.isCycleDay ? event.title : 
                           (event.title.length > 8 ? event.title.substring(0, 8) + '...' : event.title)}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event List Section */}
      <div style={{
        backgroundColor: 'white',
        margin: '16px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{fontSize: '14px', fontWeight: '600'}}>
            SLP Pocket ▼ Main ▼
          </div>
          <div style={{fontSize: '14px'}}>
            General ▼
          </div>
        </div>

        <div style={{padding: '16px'}}>
          {/* Filter Summary */}
          <div style={{marginBottom: '16px'}}>
            <div style={{
              backgroundColor: '#4a7c59',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '14px',
              marginBottom: '8px',
              display: 'inline-block'
            }}>
              {currentYear}年{currentMonth + 1}月 校曆表
            </div>
            
            {selectedEventCategory !== 'All' && (
              <div style={{
                backgroundColor: eventCategories[selectedEventCategory]?.color || '#6b7280',
                color: 'white',
                padding: '6px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                marginLeft: '8px',
                display: 'inline-block'
              }}>
                {eventCategories[selectedEventCategory]?.icon} {eventCategories[selectedEventCategory]?.name}
              </div>
            )}
          </div>

          {/* Event List */}
          <div style={{marginTop: '16px'}}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px'
            }}>
              {currentYear}年{currentMonth + 1}月活動一覽
            </div>
            
            {getEventsByMonth(currentYear, currentMonth + 1)
              .filter(event => {
                if (selectedEventCategory !== 'All' && event.category !== selectedEventCategory) return false;
                if (selectedEventType !== 'All' && event.type !== selectedEventType) return false;
                return true;
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map(event => {
                const categoryInfo = eventCategories[event.category] || {};
                const typeInfo = eventTypes[event.type] || {};
                const eventDate = new Date(event.date);
                
                return (
                  <div key={event.id} style={{
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    borderLeft: `4px solid ${categoryInfo.color || '#6b7280'}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontSize: '20px',
                        marginRight: '8px'
                      }}>
                        {categoryInfo.icon || '📅'}
                      </span>
                      <span style={{
                        fontWeight: '600',
                        color: '#1f2937',
                        fontSize: '14px'
                      }}>
                        {event.title}
                      </span>
                      {event.titleEn && (
                        <span style={{
                          color: '#6b7280',
                          fontSize: '12px',
                          marginLeft: '8px'
                        }}>
                          ({event.titleEn})
                        </span>
                      )}
                    </div>
                    
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      📅 {eventDate.getDate()}日 ({['週日', '週一', '週二', '週三', '週四', '週五', '週六'][eventDate.getDay()]})
                      {event.duration && event.duration > 1 && ` • ${event.duration}天`}
                    </div>
                    
                    {event.description && (
                      <div style={{
                        fontSize: '12px',
                        color: '#4b5563',
                        marginBottom: '4px'
                      }}>
                        {event.description}
                      </div>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '11px',
                      color: '#9ca3af'
                    }}>
                      <span style={{
                        backgroundColor: categoryInfo.color || '#6b7280',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {categoryInfo.name || event.category}
                      </span>
                      <span style={{
                        backgroundColor: '#e5e7eb',
                        color: '#4b5563',
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {typeInfo.name || event.type}
                      </span>
                      {event.targetAudience && (
                        <span>
                          👥 {event.targetAudience.join(', ')}
                        </span>
                      )}
                      {event.location && (
                        <span>
                          📍 {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            
            {getEventsByMonth(currentYear, currentMonth + 1).length === 0 && (
              <div style={{
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: '14px',
                padding: '20px'
              }}>
                本月沒有活動安排
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolEventPage;
